import React from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import {
  CheckCircle as CheckCircleIcon,
  Eye as EyeIcon,
  EyeOff as EyeOffIcon,
  FileText as FileTextIcon,
  Link2 as Link2Icon,
  UserCheck as UserCheckIcon,
} from 'react-feather'
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Tooltip,
  Typography,
} from '@material-ui/core'

import { DocumentListShortcut } from 'components'

import helpers from 'helpers'

import useStyles from './styles'
import constants from 'constants/index'

const {
  ACTIVE_STATUS_ID,
  AWAITING_APPROVAL_STATUS_ID,
  CHIP_STATUS_LABEL,
  DRAFT_STATUS_ID,
  EXPIRED_STATUS_ID,
  INACTIVE_STATUS_ID,
} = constants.documents

const STATUS_CLASS_NAMES = {
  [ACTIVE_STATUS_ID]: 'statusActive',
  [DRAFT_STATUS_ID]: 'statusDraft',
  [INACTIVE_STATUS_ID]: 'statusInactive',
  [EXPIRED_STATUS_ID]: 'statusExpired',
  [AWAITING_APPROVAL_STATUS_ID]: 'statusAwaitingApproval',
}

const CompanyDocumentItem = ({
  privacyPolicy,
  isLinked = false,
  isSelected = false,
  isLinking = false,
  disabledLink = false,
  disabledInputs = false,
  onSelect = () => {},
  onLink = () => {},
}) => {
  const classes = useStyles()

  const status = privacyPolicy?.status
  const isActive = status === ACTIVE_STATUS_ID

  const statusClass =
    classes[STATUS_CLASS_NAMES[status] || 'statusUnknown'] ||
    classes.statusUnknown

  // Mesma composição usada na tabela de controles do documento: versão maior e revisão.
  const documentVersion = privacyPolicy?.version
    ? `${privacyPolicy.version}.${privacyPolicy.review || 0}`
    : null

  const mountLinkTooltip = () => {
    if (disabledInputs) {
      return 'Esta evidência não pode ser alterada no momento.'
    }

    if (!isActive) {
      return 'Somente documentos ativos podem ser vinculados como evidência.'
    }

    return ''
  }

  const linkTooltip = mountLinkTooltip()

  return (
    <Box
      className={clsx(classes.row, { [classes.selectedRow]: isSelected })}
      data-testid="company-document-item"
    >
      <Box className={classes.info}>
        <Box className={classes.fileIcon}>
          <FileTextIcon size={20} />
        </Box>
        <Box className={classes.infoText}>
          <Box className={classes.titleLine}>
            <Typography className={classes.title} noWrap>
              {privacyPolicy?.title}
            </Typography>
            {!!documentVersion && (
              <Typography variant="caption" color="secondary">
                v{documentVersion}
              </Typography>
            )}
          </Box>
          <Box className={classes.metaLine}>
            {/* O identificador é o mesmo campo que filtra a listagem de Documentos e, quando a
                organização gerou o mesmo modelo mais de uma vez, é o que distingue documentos de
                título idêntico. */}
            <Typography variant="caption" color="secondary">
              ID {privacyPolicy?.id} &middot; Criado em&nbsp;
              {helpers.formatters.dates.ptBrFormatDate(
                privacyPolicy?.createdAt,
              )}
            </Typography>
            <DocumentListShortcut
              privacyPolicyId={privacyPolicy?.id}
              discreet
            />
            {privacyPolicy?.approved && (
              <Box className={classes.approvedLabel}>
                <UserCheckIcon size={12} />
                <Typography variant="caption">Aprovado</Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
      <Box className={classes.statusSlot}>
        <Chip
          size="small"
          label={CHIP_STATUS_LABEL[status] || 'indefinido'}
          className={clsx(classes.statusChip, statusClass)}
        />
      </Box>
      <Box className={classes.actions}>
        <Button
          color="secondary"
          size="small"
          className={classes.previewButton}
          startIcon={
            isSelected ? <EyeOffIcon size={16} /> : <EyeIcon size={16} />
          }
          onClick={onSelect}
        >
          {isSelected ? 'Ocultar' : 'Visualizar'}
        </Button>
        <Box className={classes.linkSlot}>
          {isLinked ? (
            <Chip
              size="small"
              variant="outlined"
              icon={<CheckCircleIcon size={14} />}
              label="Vinculado"
              className={classes.linkedChip}
            />
          ) : (
            <Tooltip title={linkTooltip}>
              <Box>
                <Button
                  color="primary"
                  variant="contained"
                  size="small"
                  className={classes.linkButton}
                  startIcon={
                    isLinking ? (
                      <CircularProgress size={14} color="inherit" />
                    ) : (
                      <Link2Icon size={16} />
                    )
                  }
                  onClick={onLink}
                  disabled={!!linkTooltip || disabledLink}
                >
                  {isLinking ? 'Vinculando' : 'Vincular'}
                </Button>
              </Box>
            </Tooltip>
          )}
        </Box>
      </Box>
    </Box>
  )
}

CompanyDocumentItem.propTypes = {
  privacyPolicy: PropTypes.object.isRequired,
  isLinked: PropTypes.bool,
  isSelected: PropTypes.bool,
  isLinking: PropTypes.bool,
  disabledLink: PropTypes.bool,
  disabledInputs: PropTypes.bool,
  onSelect: PropTypes.func,
  onLink: PropTypes.func,
}

export default CompanyDocumentItem
